import { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Icon from '../Icon/Icon';

const SECONDARY_TAG = 'span';

function Expandable(props) {
  const {
    children,
    className,
    header,
    headerTag,
    id,
    secondary,
    initiallyCollapsed,
    onExpandChange,
  } = props;

  const [expanded, setExpanded] = useState(props.expanded || !initiallyCollapsed);
  const HeaderTag = secondary ? SECONDARY_TAG : headerTag;

  const onKeyDown = (e) => {
    // Capture Enter and Space keys
    if (['Enter', ' '].includes(e.key)) {
      e.preventDefault();
      toggleExpansion(e);
    }
  }

  const toggleExpansion = (e) => {
    e.stopPropagation();

    setExpanded(!expanded);

    if (props.onExpandChange) {
      onExpandChange();
    }
  }

  const headerClass = (secondary) => classNames({
    'py-3 px-4 hover:bg-medium-fill-grey': true,
    'bg-light-border-grey': !secondary,
    'border-b-2 border-light-border-grey text-xs': secondary,
    'text-xs uppercase font-bold': headerTag === 'h5' && !secondary,
  });

  const bodyClass = (expanded, secondary) => classNames('py-3 px-4 text-sm', {
    'hidden': !expanded,
    'block': expanded,
    'border-b-2 border-light-border-grey': secondary,
  });

  const chevronClass = (expanded) => classNames({
    'float-right flex-none': true,
    'transform rotate-0': expanded,
    'transform rotate-180': !expanded,
  });

  return (
    <section
      className={className}
      {...(id ? { id } : {})}
    >
      <div>
        <button
          className="w-full text-left text-text-blue"
          onClick={toggleExpansion}
          onKeyDown={onKeyDown}
          id={`${id}-button`}
          tabIndex={0}
          aria-expanded={expanded}
        >
          <div className={headerClass(secondary)}>
            <HeaderTag className="flex flex-no-wrap justify-between items-center">
              <div className="flex-auto font-regular-font font-semibold">{header}</div>
              <Icon icon="ChevronArrow" color="medium-grey" size={12} className={chevronClass(expanded)} />
            </HeaderTag>
          </div>
        </button>
        <div className={bodyClass(expanded, secondary)}>
          {children}
        </div>
      </div>
    </section>
  )
};

Expandable.propTypes = {
  /** children for content inside */
  children: PropTypes.node,
  /** optional className for reference */
  className: PropTypes.string,
  /** Whether this card is expanded. */
  expanded: PropTypes.bool,
  /** string or node header used as the toggle for the main component */
  header: PropTypes.node,
  /** tag string used for semantic headings, it will not affect the styles */
  headerTag: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
  /** optional id for reference */
  id: PropTypes.string,
  /** boolean to determine if container is rendered expanded */
  initiallyCollapsed: PropTypes.bool,
  /** function that is called when toggleExpansion is called */
  onExpandChange: PropTypes.func,
  /** determines style and color of expandable container */
  secondary: PropTypes.bool,
};

Expandable.defaultProps = {
  children: [],
  className: '',
  expanded: null,
  headerTag: 'h5',
  id: '',
  initiallyCollapsed: false,
  onExpandChange: null,
  secondary: false,
};

export default Expandable;
